import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, Settings } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminParametreler = () => {
  const [parametreler, setParametreler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  const getAuthHeader = () => {
    const auth = sessionStorage.getItem("adminAuth");
    return auth ? { Authorization: `Basic ${auth}` } : {};
  };

  const fetchParametreler = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/parametreler`, {
        headers: getAuthHeader(),
      });
      setParametreler(response.data);
    } catch (error) {
      console.error("Error fetching parameters:", error);
      toast.error("Parametreler yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchParametreler();
  }, [fetchParametreler]);

  const handleUpdate = async (parametre) => {
    setSaving((prev) => ({ ...prev, [parametre.id]: true }));

    try {
      await axios.put(`${API}/admin/parametreler/${parametre.id}`, parametre, {
        headers: getAuthHeader(),
      });
      toast.success("Parametre güncellendi");
    } catch (error) {
      console.error("Error updating parameter:", error);
      toast.error("Parametre güncellenirken hata oluştu");
    } finally {
      setSaving((prev) => ({ ...prev, [parametre.id]: false }));
    }
  };

  const updateParametre = (id, field, value) => {
    setParametreler((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [field]: value } : p))
    );
  };

  const getParametreDescription = (name) => {
    const descriptions = {
      LISTE_YENILEME_SURESI: "Fiyat listesinin kaç saniyede bir güncelleneceği",
      RESIM_YENILEME_SURESI: "Resimlerin kaç saniyede bir değişeceği",
      ODA_ADI: "Oda/Kurum adı",
      BASKAN_ADI: "Başkan veya yetkili adı",
    };
    return descriptions[name] || "";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-parametreler-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6 lg:mb-8">
        <div className="w-10 h-10 lg:w-12 lg:h-12 bg-purple-100 rounded-xl flex items-center justify-center">
          <Settings className="w-5 h-5 lg:w-6 lg:h-6 text-purple-600" />
        </div>
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-slate-800" style={{ fontFamily: 'Playfair Display, serif' }}>
            Parametre Yönetimi
          </h1>
          <p className="text-slate-500 text-xs lg:text-sm">Sistem ayarlarını yapılandırın</p>
        </div>
      </div>

      {/* Parameters Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        {parametreler.map((parametre) => (
          <div 
            key={parametre.id}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 lg:p-6"
            data-testid={`parametre-card-${parametre.id}`}
          >
            <div className="flex items-start justify-between mb-3 lg:mb-4">
              <div>
                <h3 className="text-base lg:text-lg font-semibold text-slate-800">
                  {parametre.parametre_adi}
                </h3>
                <p className="text-slate-500 text-xs lg:text-sm">
                  {getParametreDescription(parametre.parametre_adi)}
                </p>
              </div>
            </div>

            <div className="space-y-3 lg:space-y-4">
              {/* Boolean value */}
              {parametre.deger !== null && parametre.deger !== undefined && (
                <div className="flex items-center gap-3">
                  <Switch
                    id={`deger-${parametre.id}`}
                    checked={parametre.deger}
                    onCheckedChange={(checked) =>
                      updateParametre(parametre.id, "deger", checked)
                    }
                    data-testid={`switch-deger-${parametre.id}`}
                  />
                  <Label htmlFor={`deger-${parametre.id}`} className="text-sm">
                    {parametre.deger ? "Aktif" : "Pasif"}
                  </Label>
                </div>
              )}

              {/* String value */}
              <div>
                <Label htmlFor={`chardeger-${parametre.id}`} className="text-sm">Değer</Label>
                <Input
                  id={`chardeger-${parametre.id}`}
                  value={parametre.char_deger || ""}
                  onChange={(e) =>
                    updateParametre(parametre.id, "char_deger", e.target.value)
                  }
                  placeholder="Değer girin"
                  className="text-sm"
                  data-testid={`input-chardeger-${parametre.id}`}
                />
              </div>

              <Button
                onClick={() => handleUpdate(parametre)}
                disabled={saving[parametre.id]}
                className="w-full btn-primary text-sm"
                data-testid={`save-parametre-${parametre.id}`}
              >
                {saving[parametre.id] ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Kaydediliyor...</span>
                  </div>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Kaydet
                  </>
                )}
              </Button>
            </div>
          </div>
        ))}
      </div>

      {parametreler.length === 0 && (
        <div className="text-center py-8 lg:py-12 bg-white rounded-xl border border-slate-200">
          <Settings className="w-10 h-10 lg:w-12 lg:h-12 text-slate-300 mx-auto mb-3 lg:mb-4" />
          <p className="text-slate-500 text-sm lg:text-base">Henüz parametre bulunmuyor</p>
        </div>
      )}
    </div>
  );
};

export default AdminParametreler;
